import { useCallback } from 'react'

import {
  useAddPanelTab,
  useChatAgentName,
  useChatEnvironmentStoreContext,
  useChatOrgId,
  useSetActiveTabId,
  useSetSourceViewerState,
  useUpdateTabNavById,
} from '@/components/chat-v2/hooks/useChatEnvironment'
import { getFileDetailEntrypoint } from '@/components/chat-v2/lib/panelEntrypoints'

import type { Source } from '@/types/chat-v2'
import type { File, PotentiallyUnhydratedFile } from '@/types/File'

let sourceActivationSequence = 0

const createSourceActivationKey = () => {
  sourceActivationSequence += 1
  return sourceActivationSequence
}

// Top-level `activeSourceId` is derived from the active tab's `nav.activeSourceId`
// (see `getPanelActiveState` in ChatEnvironmentProvider). To open a citation we
// only need to (1) write the source id onto the target tab's nav and
// (2) ensure that tab is active — we don't call `setActiveSourceId` directly.
const useOpenSourceInViewer = () => {
  const addPanelTab = useAddPanelTab()
  const agentName = useChatAgentName()
  const store = useChatEnvironmentStoreContext()
  const setSourceViewerState = useSetSourceViewerState()
  const setActiveTabId = useSetActiveTabId()
  const updateTabNavById = useUpdateTabNavById()

  return useCallback(
    (
      id: number,
      source?: Source | null,
      file?: PotentiallyUnhydratedFile | null,
    ) => {
      setSourceViewerState('open')

      if (source?.source_type === 'file') {
        const { panelTabs } = store.getState()
        const existingTab =
          panelTabs
            .filter(
              (tab) =>
                tab.nav?.view === 'detail' && tab.nav.fileId === source.file_id,
            )
            .at(-1) ?? null
        const entrypointId =
          existingTab?.entrypointId ??
          getFileDetailEntrypoint(agentName, file?.type)
        const sourceActivationKey = createSourceActivationKey()

        let tabId: string
        if (existingTab) {
          tabId = existingTab.id
          // Activating syncs top-level activeSourceId from this tab's nav.
          setActiveTabId(tabId)
        } else {
          // addPanelTab(reuseExisting:false) creates AND activates the tab.
          tabId = addPanelTab(entrypointId, { reuseExisting: false })
        }
        // Tab is active here; updating nav re-derives top-level activeSourceId.
        updateTabNavById(tabId, {
          view: 'detail',
          fileId: source.file_id,
          fileType: file?.type,
          fileName: file?.name ?? source.file_name ?? 'Document',
          activeSourceId: id,
          activeSourceSnapshot: source,
          sourceActivationKey,
          pdfScrollTop: undefined,
        })
        return
      }

      // Reuses-and-activates the most recent citations tab, or creates one.
      const tabId = addPanelTab('citations')
      const sourceActivationKey = createSourceActivationKey()
      // Write the source id to nav so reactivating this tab later restores it.
      updateTabNavById(tabId, {
        activeSourceId: id,
        activeSourceSnapshot: source ?? undefined,
        sourceActivationKey,
      })
    },
    [
      addPanelTab,
      agentName,
      setActiveTabId,
      setSourceViewerState,
      store,
      updateTabNavById,
    ],
  )
}

export const useSourceIdClick = () => {
  return useOpenSourceInViewer()
}

export const useSourceClick = (
  source: Source | undefined | null,
  file?: PotentiallyUnhydratedFile | null,
) => {
  const openSourceInViewer = useOpenSourceInViewer()
  const orgId = useChatOrgId()

  return useCallback(
    (id: number) => {
      if (!source) {
        return
      }
      const sourceType = source.source_type
      switch (sourceType) {
        case 'web': {
          const win = window.open(source.url, '_blank', 'noopener,noreferrer')
          if (win) win.opener = null
          return
        }
        case 'entity':
          // TODO: implement entity click
          return
        case 'file':
          openSourceInViewer(id, source, file)
          return
        case 'tool':
          openSourceInViewer(id)
          return
        case 'image':
          // TODO: implement image click (maybe show in modal?)
          return
        case 'pursuit': {
          if (!orgId) return
          const url = `/orgs/${orgId}/pursuits/${source.pursuit_id}`
          const win = window.open(url, '_blank', 'noopener,noreferrer')
          if (win) win.opener = null
          return
        }
        case 'explore':
          // Explore sources are parent groupings - clicking shows their children
          openSourceInViewer(id)
          return
        default:
          // Exhaustive check - this should never be reached
          const _exhaustiveCheck: never = sourceType
          return _exhaustiveCheck
      }
    },
    [source, file, openSourceInViewer, orgId],
  )
}
